import { Dispatch, SetStateAction } from 'react';
import { MessageDescriptor } from 'react-intl';
import { UseQueryResult } from '@tanstack/react-query';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faCalendarAlt,
  faCalendarCheck,
} from '@fortawesome/free-solid-svg-icons';
import { faCalendarExclamation } from '@fortawesome/pro-regular-svg-icons';

export enum ROUTE {
  root = '/',
  search = '/search',
  advancedSearch = '/advanced-search',
  login = '/login',
  directLogin = '/direct-login',
  projects = '/projects',
  projectsDetails = '/projects/:id',
  projectsDetailsSearches = '/projects/:id/searches',
  projectsDetailsRecords = '/projects/:id/records',
  createAccount = '/create-account',
  preregistration = '/preregistration',
  logout = '/logout',
  instanceDetails = '/instances/:id',
  requestsAndLoans = '/requests-and-loans',
  holdsAndCheckouts = '/holds-and-checkouts',
  holds = '/holds',
  checkouts = '/checkouts',
  requests = '/requests',
  loans = '/loans',
  fees = '/fees',
  courseReserves = '/course-reserves',
  courseReserveDetails = '/course-reserves/:id',
  sso = '/sso-landing',
  callNumbers = '/call-numbers',
  preferences = '/my-preferences',
  notFound = '/not-found',
  resetPinRoot = '/reset-pin',
  enterNewPin = '/reset-pin/pin',
  enterBarcode = '/reset-pin/barcode',
  recentActivity = '/recent-activity',
  recentSearchHistory = '/recent-activity/search-history',
  recentViewedRecords = '/recent-activity/viewed-records',
  saved = '/saved',
  savedSearches = '/saved/searches',
  savedRecords = '/saved/records',
}

export enum FEATURE {
  holdsAndCheckouts = 'HOLDS_AND_CHECKOUTS',
  courseReserves = 'COURSE_RESERVES',
  staffView = 'STAFF_VIEW',
  virtualBookshelf = 'VIRTUAL_BOOKSHELF',
  novelist = 'NOVELIST',
  callNumbers = 'CALL_NUMBERS',
  instanceRelationship = 'INSTANCE_RELATIONSHIP',
  statusSearchFacet = 'STATUS_SEARCH_FACET',
  placeHold = 'PLACE_HOLD',
  placeHoldNotes = 'PLACE_HOLD_NOTES',
  publicationYearFacet = 'PUBLICATION_YEAR_FACET',
  consortiaLoginFlow = 'CONSORTIA_LOGIN_FLOW',
  RTACForSearch = 'RTAC_FOR_SEARCH',
  LCCNSearch = 'LCCN_SEARCH',
  passthroughlink = 'PASSTHROUGH_LINK',
  preregistration = 'PREREGISTRATION',
  placeHoldLibraryNote = 'PLACE_HOLD_LIBRARY_NOTE',
  LCLinkResolver = 'LC_LINK_RESOLVER',
}

export interface FeatureFlagToMatch {
  name: FEATURE;
  isActiveIfNotFound?: boolean;
}

export enum USER_SESSION {
  guest = 'guestUserSession',
  patron = 'patronUserSession',
}

export const BREAKPOINT = {
  CONTAINER: {
    MINIMUM_SCREEN_SIZE: 274,
    PHONE: 382,
    TABLET_SM: 738,
    TABLET_LG: 960,
    DESKTOP_SM: 1180,
    DESKTOP_MD: 1428,
    DESKTOP_LG: 1600,
  },
  SCREEN: {
    MINIMUM_SCREEN_SIZE: 0,
    PHONE: 411,
    TABLET_SM: 767,
    TABLET_LG: 1023,
    DESKTOP_SM: 1239,
    DESKTOP_MD: 1499,
    DESKTOP_LG: 1919,
  },
};

export const SUPPORT_SITE_URL = 'https://connect.ebsco.com/s/?language=en_US';

export enum SORT_DIRECTION {
  asc = 'ascending',
  desc = 'descending',
}

export enum SORT_DIRECTION_REVERSED {
  ascending = 'asc',
  descending = 'desc',
}

export const ADVANCED_SEARCH_ERROR = {
  CODE: 'validation_error',
  TYPE: 'RequestValidationException',
};

export const BACK_TO_TOP_TRIGGER_POSITION = 300;

export const MIN_SUGGESTION_TERM_LENGTH = 3;

export const IS_TEST_ENV = process.env.NODE_ENV === 'test';

export const RECORDS_PER_PAGE = IS_TEST_ENV ? 2 : 25;

export const MODAL_PLACEMENT = IS_TEST_ENV ? 'body > div' : '#root';

export const LOGIN_METHODS = {
  SSO: 'SSO only',
  NATIVE: 'Native only',
};

export interface URLQuery {
  [key: string]: string | string[];
}

export interface SortOption {
  label: MessageDescriptor;
  sortField: string;
  sortDirection?: SORT_DIRECTION;
  icon?: IconDefinition;
}

export enum NOTIFICATION_STATUS {
  success = 'success',
  error = 'error',
  information = 'information',
}

export const FOCUSED_ELEMENT_SELECTOR = '[data-focus=focusedNode]';

export enum DefaultCoverImageTypes {
  'Books' = 'book',
  'Videos' = 'video',
  'Electronic Resources' = 'eBook',
  'Audio' = 'audio',
  'nonCatalog' = 'nonCatalog',
}

export const ENGLISH_LOCALE_KEY = 'en';

// eslint-disable-next-line @typescript-eslint/ban-types
export type PropsWithClassName<P = {}> = P & { className?: string };

export enum LOGIN_METHOD {
  native = 'native',
  direct = 'direct',
  sso = 'sso',
}

export enum PASSWORD_TYPE {
  pin = 'pin',
  password = 'password',
}

export const INVOKE_PATH_PREFIX = `/_/invoke/tenant/${process.env.REACT_APP_API_TENANT}`;

export type SetState<Type> = Dispatch<SetStateAction<Type>>;

export const FETCH_LIMIT = 1000;

export const searchInterface = 'LOCATE Catalog';

export interface InstanceItemPublication {
  dateOfPublication: string;
  publisher?: string;
  place?: string;
  role?: string;
}

export interface InstanceItem {
  instanceId: string;
  itemId: string;
  title: string;
  authors?: string[];
  isbns?: string[];
  sourceTypes?: string[];
  publication?: InstanceItemPublication[];
  virtual?: boolean;
}

export interface HoldDto {
  item: InstanceItem;
  pickupLocationId: string;
  queuePosition: number;
  requestDate: string;
  requestId: string;
  status: string;
}

export interface CheckoutDto {
  dueDate: string;
  id: string;
  item: InstanceItem;
  loanDate: string;
  overdue: boolean;
  amount?: number;
  isoCurrencyCode?: string;
}

export type ItemWithStatus = HoldDto | CheckoutDto;

export interface ChargeDto {
  accrualDate: string;
  state: string;
  reason: string;
  chargeAmount: {
    amount: number;
    isoCurrencyCode: string;
  };
  item?: InstanceItem;
}

export interface ItemStatusDto {
  totalHolds: number;
  totalLoans: number;
  totalChargesCount: number;
  totalCharges: {
    amount: number;
    isoCurrencyCode: string;
  };
  holds?: HoldDto[];
  loans?: CheckoutDto[];
  charges?: ChargeDto[];
}

export interface ItemStatusData {
  itemId: string;
  icon: IconDefinition;
  status: string;
  isNonCatalogItem?: boolean;
  instanceId?: string;
  actionButtonText?: string;
  statusMessage?: string;
  feesTitle?: string;
  feesValue?: string;
  holdId?: string;
  isVirtualItem?: boolean;
}

export interface DepartmentInstructorItemDto {
  id: string;
  name: string;
}

export enum ITEM_STATUS {
  onHold = 'On hold',
  checkedOut = 'Checked out',
  overdue = 'Overdue',
}

export const STATUS_ICON = {
  [ITEM_STATUS.onHold]: faCalendarAlt,
  [ITEM_STATUS.checkedOut]: faCalendarCheck,
  [ITEM_STATUS.overdue]: faCalendarExclamation,
};

export interface Resource<PageType, QueryResultType> {
  pages: PageType[][];
  recordKey: keyof PageType;
  totalRecords: number;
  queryResult: UseQueryResult<QueryResultType>;
  next?: string;
}

export const QUICK_FILTER_TYPE = {
  allTime: 'allTime',
  pastYear: 'pastYear',
  pastFiveYears: 'pastFiveYears',
  pastTenYears: 'pastTenYears',
  custom: 'custom',
} as const;

export const supportedLocales = [
  { type: 'English', label: 'English', id: 'en' },
  { type: 'German', label: 'Deutsch', id: 'de' },
  { type: 'Spanish', label: 'Español', id: 'es' },
  { type: 'Greek', label: 'Ελληνικά', id: 'el' },
  { type: 'French', label: 'Français', id: 'fr' },
  { type: 'Italian', label: 'Italiano', id: 'it' },
  { type: 'Hungarian', label: 'Magyar', id: 'hu' },
  { type: 'Polish', label: 'Polski', id: 'pl' },
  { type: 'Portuguese (Brasilia)', label: 'Português (Brasil)', id: 'pt-BR' },
  {
    type: 'Portuguese (Portuguese)',
    label: 'Português (Portugal)',
    id: 'pt-PT',
  },
  { type: 'Russian', label: 'Русский', id: 'ru' },
  { type: 'Turkish', label: 'Türkçe', id: 'tr' },
  { type: 'Arabic', label: 'عربي', id: 'ar' },
  { type: 'Simplified Chinese', label: '简体中文', id: 'zh-CN' },
  { type: 'Traditional Chinese', label: '繁體中文', id: 'zh-TW' },
  { type: 'Japanese', label: '日本語', id: 'ja' },
  { type: 'Korean', label: '한국어', id: 'ko' },
  { type: 'Thai', label: 'ภาษาไทย', id: 'th' },
  { type: 'Vietnamese', label: 'Tiếng Việt', id: 'vi' },
  { type: 'Croatian', label: 'Hrvatski', id: 'hr' },
  { type: 'Czech', label: 'Čeština', id: 'cs' },
  { type: 'Indonesian', label: 'Bahasa Indonesia', id: 'id' },
  { type: 'Hebrew', label: 'עִבְרִית', id: 'he' },
  { type: 'Romanian', label: 'Română', id: 'ro' },
  { type: 'Slovenian', label: 'Slovenski', id: 'sl' },
  { type: 'Slovak', label: 'Slovenčina', id: 'sk' },
  { type: 'Dutch', label: 'Nederlands', id: 'nl' },
  { type: 'Swedish', label: 'Svenska', id: 'sv' },
  { type: 'Farsi', label: 'فارسی', id: 'fa' },
  { type: 'Finnish', label: 'Suomi', id: 'fi' },
  { type: 'Danish', label: 'Dansk', id: 'da' },
  { type: 'Norwegian', label: 'Norsk', id: 'no' },
];

export const SSO_REDIRECT_ROUTE_KEY = 'redirectRoute';

export type IntlMessages = Record<string, string>;

export const HOLDS_AND_CHECKOUTS_TABS = {
  holds: 'holds',
  checkouts: 'checkouts',
  fees: 'fees',
} as const;

export type HoldsAndCheckoutsTab = keyof typeof HOLDS_AND_CHECKOUTS_TABS;

export const defaultDebounceTimeMs = 300;
